import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon, PrinterIcon } from '@heroicons/react/solid';
import { DashHeader, Footer, LoadingOverlay, Signin } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { clientNoCdn } from '../clientNoCdn';
import { recordQuery, historyQuery } from '../utils/Data';
import NoAccess from './NoAccess';

// REV-003: printable advertisement record for one job — where and when it was
// advertised, with exact dates (LMIA employers use this as proof of advertising).

// Audience wording and job ID prefix match what each board shows on its job page.
const BOARDS = {
  NewJobLeads: { host: 'www.newjobleads.ca', prefix: 'NJL', audience: 'New Immigrants, Newcomers to Canada' },
  YouthJobLeads: { host: 'www.youthjobleads.ca', prefix: 'YJL', audience: 'Canadian Youth' },
  AbleJobLeads: { host: 'www.ablejobleads.ca', prefix: 'ABJL', audience: 'People with disabilities' },
  RefugeeJobLeads: { host: 'refugeejobleads.ca', prefix: 'RJL', audience: 'Refugee status holders in Canada' },
  AboriginalJobLeads: { host: 'www.aboriginaljobleads.ca', prefix: 'AJL', audience: 'Aboriginal/Indigenous People' },
};

// Exact calendar date, never relative (D-013).
function isoDate(value) {
  return value ? String(value).slice(0, 10) : '—';
}

// Plain-text rendering of Portable Text blocks, so nothing in a posting can inject markup.
function PlainBody({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return <p>—</p>;
  return blocks
    .filter((block) => block && block._type === 'block')
    .map((block, i) => {
      const text = (block.children || []).map((child) => child.text || '').join('');
      if (!text.trim()) return null;
      return block.listItem ? (
        <p key={block._key || i} className="ml-4">
          • {text}
        </p>
      ) : (
        <p key={block._key || i} className="mb-2">
          {text}
        </p>
      );
    });
}

const Row = ({ label, children }) => (
  <tr className="border-b border-gray-200">
    <th className="py-2 pr-4 text-left align-top font-semibold text-gray-700 w-48">{label}</th>
    <td className="py-2 text-gray-900">{children}</td>
  </tr>
);

function AdRecord() {
  const { jobId } = useParams();
  const { currentUser, signout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    Promise.all([
      clientNoCdn.fetch(recordQuery(jobId)),
      clientNoCdn.fetch(historyQuery),
    ])
      .then(([post, log]) => {
        setJob(post || null);
        const entries = (log || []).filter(
          (entry) =>
            entry &&
            (entry.jobId === jobId || (entry.productId || '').includes(jobId)) &&
            (entry.action === 'extend' || entry.action === 'renew')
        );
        entries.sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
        setHistory(entries);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [currentUser, jobId]);

  function handleSignout() {
    try {
      signout();
      navigate('/');
    } catch (err) {
      console.log(err);
    }
  }

  if (loading) return <LoadingOverlay />;
  if (!currentUser) return <Signin />;
  if (!job || job.employerRef !== currentUser.uid) return <NoAccess />;

  const shortId = `${job._id.slice(0, 4)}${job._id.slice(32)}`;
  const boards = (job.postAt || []).filter((board) => BOARDS[board]);
  const generatedAt = new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

  return (
    <>
      <div className="print:hidden">
        <DashHeader handle={handleSignout} userEmail={currentUser?.email} />
      </div>

      {/* Page margins for print, scoped to this page while it is mounted */}
      <style>{'@page { margin: 18mm 16mm; }'}</style>

      <main className="max-w-4xl mx-auto px-4 py-8 print:max-w-none print:px-2 print:py-2">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/dashboard" className="inline-flex items-center font-semibold text-gray-900">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            Back to dashboard
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold hover:opacity-90"
          >
            <PrinterIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Print / Save as PDF
          </button>
        </div>

        <header className="mb-6 print:mb-8 print:pb-4 print:border-b print:border-gray-300">
          <p className="text-sm font-semibold text-primary">CanadaJobLeads</p>
          <h1 className="text-2xl font-bold text-gray-900">Job Advertisement Record</h1>
          <p className="text-sm text-gray-500">Generated {generatedAt}</p>
        </header>

        <section className="mb-8 print:mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Job details</h2>
          <table className="w-full text-sm">
            <tbody>
              <Row label="Job title">{job.title || '—'}</Row>
              <Row label="Company">{job.companyName || '—'}</Row>
              <Row label="Location">
                {[job.location, job.city, job.province].filter(Boolean).join(', ') || '—'}
              </Row>
              <Row label="Salary">{job.salary || '—'}</Row>
              <Row label="Employment type">{job.categories || '—'}</Row>
              <Row label="Vacancies">{job.vacancies || '—'}</Row>
              <Row label="Application email">{job.email || '—'}</Row>
              <Row label="Record ID">{job._id}</Row>
            </tbody>
          </table>
        </section>

        <section className="mb-8 print:mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Advertising period</h2>
          <table className="w-full text-sm">
            <tbody>
              <Row label="First published">{isoDate(job._createdAt)}</Row>
              <Row label="Current posting date">{isoDate(job.postedOn)}</Row>
              <Row label="Expiry date">{isoDate(job.postExpiry)}</Row>
              <Row label="Applications received">{`${job.applications || 0} through the job boards`}</Row>
            </tbody>
          </table>
          {history.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-1">Extensions and renewals</h3>
              <ul className="text-sm text-gray-900 list-disc ml-5">
                {history.map((entry, i) => (
                  <li key={entry._key || i}>
                    {entry.action === 'renew' ? 'Renewed' : 'Extended'} on {isoDate(entry.timestamp)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="mb-8 print:mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Where it was advertised</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300 text-left">
                <th className="py-2 pr-4">Job board</th>
                <th className="py-2 pr-4">Audience</th>
                <th className="py-2 pr-4">Job ID shown</th>
                <th className="py-2">Link</th>
              </tr>
            </thead>
            <tbody>
              {boards.map((board) => {
                const { host, prefix, audience } = BOARDS[board];
                const url = `https://${host}/jobs/${job._id}`;
                return (
                  <tr key={board} className="border-b border-gray-200 align-top">
                    <td className="py-2 pr-4 font-semibold">{board}</td>
                    <td className="py-2 pr-4">{audience}</td>
                    <td className="py-2 pr-4">
                      {prefix}-{shortId}
                    </td>
                    <td className="py-2 break-all">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {url}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section className="mb-8 print:mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Job description as posted</h2>
          <div className="text-sm text-gray-900">
            <PlainBody blocks={job.body} />
          </div>
        </section>

        <p className="text-xs text-gray-500 border-t border-gray-200 pt-4">
          This record was generated from CanadaJobLeads posting data. "Current posting date" changes when a
          job is renewed; "First published" is when the posting was first created. Links stay live until the
          expiry date.
        </p>
      </main>

      <div className="print:hidden">
        <Footer />
      </div>
    </>
  );
}

export default AdRecord;
